import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { get, post, patch } from "../../lib/request"

/** Existing record from GET has an `id`; null means create via POST. */
export function getOutOfOfficeId(record) {
  if (record == null || typeof record !== "object") return null
  const id = record.id
  if (id == null || id === "") return null
  return id
}

/**
 * API may return ISO strings or Java-style arrays: [year, month, day, hour, minute] (month 1–12).
 */
export function parseApiDatetime(value) {
  if (value == null || value === "") return null

  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value
    if (year == null || month == null || day == null) return null
    const d = new Date(year, Number(month) - 1, day, hour, minute, second)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function toDatetimeLocalValue(value) {
  const d = parseApiDatetime(value)
  if (!d) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Normalize GET/POST/PATCH payload for Redux + form binding. */
export function normalizeOutOfOfficeRecord(raw) {
  if (raw == null || typeof raw !== "object") return null
  const from = parseApiDatetime(raw.fromDatetime)
  const until = parseApiDatetime(raw.untilDatetime)
  const forwardToUserId =
    raw.forwardToUserId != null && raw.forwardToUserId !== ""
      ? raw.forwardToUserId
      : raw.forwardToUser?.id

  return {
    ...raw,
    fromDatetime: from ? from.toISOString() : raw.fromDatetime,
    untilDatetime: until ? until.toISOString() : raw.untilDatetime,
    forwardToUserId: forwardToUserId ?? null,
  }
}

export function toIsoDatetime(localValue) {
  if (!localValue) return null
  const d = new Date(localValue)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

/** Build POST/PATCH /out-of-office body: org when all properties, else clientId; optional forwardToUserId. */
export function buildOutOfOfficeSavePayload({
  selectedPropertyId,
  organizationId,
  enable,
  fromDatetime,
  untilDatetime,
  forwardToUserId,
}) {
  const payload = {
    enable: !!enable,
    fromDatetime,
    untilDatetime,
  }

  if (selectedPropertyId != null && selectedPropertyId !== "") {
    payload.clientId = Number(selectedPropertyId)
  } else if (organizationId != null && organizationId !== "") {
    payload.organizationId = Number(organizationId)
  }

  if (forwardToUserId != null && forwardToUserId !== "") {
    payload.forwardToUserId = Number(forwardToUserId)
  }

  return payload
}

export const fetchOutOfOffice = createAsyncThunk(
  "outOfOffice/fetch",
  async ({ clientId, organizationId }, { rejectWithValue }) => {
    try {
      let query = ""
      if (clientId != null && clientId !== "") {
        query = `?clientId=${encodeURIComponent(clientId)}`
      } else if (organizationId != null && organizationId !== "") {
        query = `?orgId=${encodeURIComponent(organizationId)}`
      } else {
        return rejectWithValue("clientId or organizationId required")
      }

      const res = await get(`out-of-office${query}`)
      if (res == null) return null
      const body = res?.data !== undefined ? res.data : res
      const row = Array.isArray(body) ? body[0] : body
      return normalizeOutOfOfficeRecord(row) ?? null
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || "Failed to fetch out of office")
    }
  },
)

export const saveOutOfOffice = createAsyncThunk(
  "outOfOffice/save",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const body = { ...payload }
      const explicitId = body.existingOutOfOfficeId
      delete body.existingOutOfOfficeId

      const existingId =
        explicitId != null && explicitId !== ""
          ? explicitId
          : getOutOfOfficeId(getState()?.outOfOffice?.data)

      let res
      if (existingId != null && existingId !== "") {
        res = await patch(`out-of-office/${existingId}`, body)
      } else {
        res = await post("out-of-office", body)
      }

      const saved = res?.data !== undefined ? res.data : res
      const row = Array.isArray(saved) ? saved[0] : saved
      return (
        normalizeOutOfOfficeRecord(row) ?? {
          ...body,
          id: existingId ?? row?.id,
        }
      )
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || "Failed to save out of office")
    }
  },
)

const outOfOfficeSlice = createSlice({
  name: "outOfOffice",
  initialState: {
    data: null,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearOutOfOffice(state) {
      state.data = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutOfOffice.pending, (state) => {
        state.loading = true
        state.error = null
        state.data = null
      })
      .addCase(fetchOutOfOffice.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
      })
      .addCase(fetchOutOfOffice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(saveOutOfOffice.pending, (state) => {
        state.saving = true
        state.error = null
      })
      .addCase(saveOutOfOffice.fulfilled, (state, action) => {
        state.saving = false
        state.data = action.payload
      })
      .addCase(saveOutOfOffice.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload
      })
  },
})

export const { clearOutOfOffice } = outOfOfficeSlice.actions
export default outOfOfficeSlice.reducer
